import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  AcademicCapIcon,
  UserGroupIcon,
  ShoppingBagIcon,
  ChartBarIcon,
  ArrowTrendingDownIcon,
  PlusIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const RevenueAnalysis = () => {
  const [period, setPeriod] = useState('month');
  const [month, setMonth] = useState('07'); // Juillet
  const [year, setYear] = useState('2023');

  // Données fictives des revenus par source
  const revenueBySource = [
    { source: 'Abonnements', amount: 2850000, percentage: 60, trend: '+12%', color: 'blue' },
    { source: 'Sessions coaching', amount: 760000, percentage: 16, trend: '+8%', color: 'green' },
    { source: 'Vente de produits', amount: 570000, percentage: 12, trend: '+5%', color: 'purple' },
    { source: 'Services spéciaux', amount: 380000, percentage: 8, trend: '-3%', color: 'orange' },
    { source: 'Location d\'équipement', amount: 190000, percentage: 4, trend: '+15%', color: 'yellow' }
  ];

  // Données fictives des tendances mensuelles
  const monthlyTrends = [
    { month: 'Janvier', revenue: 3800000, expenses: 2300000, profit: 1500000 },
    { month: 'Février', revenue: 4100000, expenses: 2400000, profit: 1700000 },
    { month: 'Mars', revenue: 4200000, expenses: 2450000, profit: 1750000 },
    { month: 'Avril', revenue: 4350000, expenses: 2500000, profit: 1850000 },
    { month: 'Mai', revenue: 4600000, expenses: 2700000, profit: 1900000 },
    { month: 'Juin', revenue: 4700000, expenses: 2800000, profit: 1900000 },
    { month: 'Juillet', revenue: 4750000, expenses: 2850000, profit: 1900000 }
  ];

  // Données détaillées des revenus par types d'abonnements
  const subscriptionRevenues = [
    { type: 'Mensuel', count: 230, amount: 1955000, avgValue: 8500 },
    { type: 'Trimestriel', count: 45, amount: 540000, avgValue: 12000 },
    { type: 'Annuel', count: 29, amount: 290000, avgValue: 10000 },
    { type: 'Journalier', count: 130, amount: 65000, avgValue: 500 }
  ];

  // Données des revenus des sessions de coaching
  const coachingRevenues = [
    { coach: 'Ahmed Benali', sessions: 38, revenue: 304000, clients: 14 },
    { coach: 'Fatima Rahmani', sessions: 42, revenue: 294000, clients: 18 },
    { coach: 'Karim Nadji', sessions: 23, revenue: 162000, clients: 8 }
  ];

  // Calcul des totaux
  const totalRevenue = revenueBySource.reduce((sum, item) => sum + item.amount, 0);
  const totalCoachingSessions = coachingRevenues.reduce((sum, coach) => sum + coach.sessions, 0);
  const totalCoachingRevenue = coachingRevenues.reduce((sum, coach) => sum + coach.revenue, 0);

  // Formatage des nombres pour l'affichage
  const formatCurrency = (value) => value.toLocaleString('fr-DZ');
  
  // Conversion des périodes
  const periodLabels = {
    'day': 'Aujourd\'hui',
    'week': 'Cette semaine',
    'month': 'Ce mois',
    'quarter': 'Ce trimestre',
    'year': 'Cette année'
  };

  return (
    <div className="space-y-6">
      {/* En-tête avec sélection de période */}
      <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center">
            <ArrowTrendingUpIcon className="h-6 w-6 text-green-400 mr-2" />
            <h2 className="text-xl font-bold text-white">Analyse des revenus</h2>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
            >
              <option value="01" className="bg-slate-800">Janvier</option>
              <option value="02" className="bg-slate-800">Février</option>
              <option value="03" className="bg-slate-800">Mars</option>
              <option value="04" className="bg-slate-800">Avril</option>
              <option value="05" className="bg-slate-800">Mai</option>
              <option value="06" className="bg-slate-800">Juin</option>
              <option value="07" className="bg-slate-800">Juillet</option>
              <option value="08" className="bg-slate-800">Août</option>
              <option value="09" className="bg-slate-800">Septembre</option>
              <option value="10" className="bg-slate-800">Octobre</option>
              <option value="11" className="bg-slate-800">Novembre</option>
              <option value="12" className="bg-slate-800">Décembre</option>
            </select>
            
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
            >
              <option value="2023" className="bg-slate-800">2023</option>
              <option value="2022" className="bg-slate-800">2022</option>
            </select>
            
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
            >
              <option value="month" className="bg-slate-800">Mensuel</option>
              <option value="quarter" className="bg-slate-800">Trimestriel</option>
              <option value="year" className="bg-slate-800">Annuel</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Cartes de statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/20 backdrop-blur-sm rounded-xl p-6"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-blue-300 text-sm">Revenu Total</p>
              <h3 className="text-2xl font-bold text-white mt-1">{formatCurrency(totalRevenue)} DA</h3>
            </div>
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <BanknotesIcon className="h-6 w-6 text-blue-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <ArrowTrendingUpIcon className="h-4 w-4 text-green-400" />
            <span className="text-green-400 text-sm ml-1">+8.3%</span>
            <span className="text-white/50 text-xs ml-2">vs mois précédent</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 border border-purple-500/20 backdrop-blur-sm rounded-xl p-6"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-purple-300 text-sm">Nouveaux Abonnements</p>
              <h3 className="text-2xl font-bold text-white mt-1">28</h3>
            </div>
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <UserGroupIcon className="h-6 w-6 text-purple-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <ArrowTrendingUpIcon className="h-4 w-4 text-green-400" />
            <span className="text-green-400 text-sm ml-1">+15%</span>
            <span className="text-white/50 text-xs ml-2">vs mois précédent</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="bg-gradient-to-br from-green-500/20 to-green-600/20 border border-green-500/20 backdrop-blur-sm rounded-xl p-6"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-green-300 text-sm">Sessions de Coaching</p>
              <h3 className="text-2xl font-bold text-white mt-1">{totalCoachingSessions}</h3>
            </div>
            <div className="p-2 bg-green-500/20 rounded-lg">
              <AcademicCapIcon className="h-6 w-6 text-green-300" />
            </div>
          </div>
          <div className="flex items-center mt-4">
            <ArrowTrendingUpIcon className="h-4 w-4 text-green-400" />
            <span className="text-green-400 text-sm ml-1">+5.2%</span>
            <span className="text-white/50 text-xs ml-2">vs mois précédent</span>
          </div>
        </motion.div>
      </div>

      {/* Répartition des revenus par source */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.4 }}
        className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
      >
        <div className="flex items-center mb-4">
          <ChartBarIcon className="h-6 w-6 text-blue-400 mr-2" />
          <h2 className="text-xl font-bold text-white">Répartition des revenus par source</h2>
        </div>

        <div className="space-y-4">
          {revenueBySource.map((item, index) => (
            <div key={index} className="bg-white/5 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-white font-medium">{item.source}</h3>
                  <p className="text-white/60 text-sm">
                    {item.percentage}% du revenu total
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-white font-semibold">{formatCurrency(item.amount)} DA</p>
                  <p className={`text-sm ${item.trend.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>
                    {item.trend} vs mois précédent
                  </p>
                </div>
              </div>
              <div className="w-full bg-white/10 h-2 rounded-full mt-2">
                <div 
                  className={`h-2 rounded-full bg-${item.color}-500/60`}
                  style={{ width: `${item.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-white/5 rounded-lg">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-white">Revenu Total: {formatCurrency(totalRevenue)} DA</h3>
            <p className="text-white/60 mt-1">Période: {month}/{year}</p>
          </div>
        </div>
      </motion.div>

      {/* Revenus par type d'abonnement */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.5 }}
        className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
      >
        <div className="flex items-center mb-4">
          <UserGroupIcon className="h-6 w-6 text-purple-400 mr-2" />
          <h2 className="text-xl font-bold text-white">Revenus par types d'abonnements</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/10">
                <th className="pb-3 text-white font-medium">Type</th>
                <th className="pb-3 text-white font-medium">Nombre</th>
                <th className="pb-3 text-white font-medium">Montant</th>
                <th className="pb-3 text-white font-medium">Valeur moyenne</th>
                <th className="pb-3 text-white font-medium">% du total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {subscriptionRevenues.map((item, index) => (
                <tr key={index} className="hover:bg-white/5 transition-colors">
                  <td className="py-3 text-white">{item.type}</td>
                  <td className="py-3 text-white">{item.count}</td>
                  <td className="py-3 text-white">{formatCurrency(item.amount)} DA</td>
                  <td className="py-3 text-white">{formatCurrency(item.avgValue)} DA</td>
                  <td className="py-3 text-white">{((item.amount / revenueBySource[0].amount) * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-white/10">
                <td className="pt-3 text-white font-semibold">Total</td>
                <td className="pt-3 text-white font-semibold">{subscriptionRevenues.reduce((sum, item) => sum + item.count, 0)}</td>
                <td className="pt-3 text-white font-semibold">{formatCurrency(subscriptionRevenues.reduce((sum, item) => sum + item.amount, 0))} DA</td>
                <td className="pt-3 text-white"></td>
                <td className="pt-3 text-white font-semibold">100%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </motion.div>

      {/* Revenus des coachs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.6 }}
        className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
      >
        <div className="flex items-center mb-4">
          <AcademicCapIcon className="h-6 w-6 text-green-400 mr-2" />
          <h2 className="text-xl font-bold text-white">Revenus des sessions coaching</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/10">
                <th className="pb-3 text-white font-medium">Coach</th>
                <th className="pb-3 text-white font-medium">Sessions</th>
                <th className="pb-3 text-white font-medium">Clients</th>
                <th className="pb-3 text-white font-medium">Revenu</th>
                <th className="pb-3 text-white font-medium">Moy. par session</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {coachingRevenues.map((coach, index) => (
                <tr key={index} className="hover:bg-white/5 transition-colors">
                  <td className="py-3 text-white">{coach.coach}</td>
                  <td className="py-3 text-white">{coach.sessions}</td>
                  <td className="py-3 text-white">{coach.clients}</td>
                  <td className="py-3 text-white">{formatCurrency(coach.revenue)} DA</td>
                  <td className="py-3 text-white">{formatCurrency(Math.round(coach.revenue / coach.sessions))} DA</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-white/10">
                <td className="pt-3 text-white font-semibold">Total</td>
                <td className="pt-3 text-white font-semibold">{totalCoachingSessions}</td>
                <td className="pt-3 text-white font-semibold">{coachingRevenues.reduce((sum, coach) => sum + coach.clients, 0)}</td>
                <td className="pt-3 text-white font-semibold">{formatCurrency(totalCoachingRevenue)} DA</td>
                <td className="pt-3 text-white font-semibold">{formatCurrency(Math.round(totalCoachingRevenue / totalCoachingSessions))} DA</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </motion.div>

      {/* Évolution des revenus */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.7 }}
        className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
      >
        <div className="flex items-center mb-4">
          <ArrowTrendingUpIcon className="h-6 w-6 text-blue-400 mr-2" />
          <h2 className="text-xl font-bold text-white">Évolution des revenus</h2>
        </div>

        <div className="relative h-72 mt-4 bg-white/5 rounded-lg p-4">
          <div className="text-center">
            <ArrowTrendingUpIcon className="h-12 w-12 text-white/20 mx-auto" />
            <p className="mt-4 text-white/50">Graphique d'évolution des revenus sur 6 mois</p>
            <div className="mt-4">
              <div className="flex flex-wrap justify-center gap-4 text-xs">
                {monthlyTrends.map((item, index) => (
                  <div key={index} className="bg-white/10 px-3 py-2 rounded-lg">
                    <p className="text-white/60">{item.month}</p>
                    <p className="text-white font-medium mt-1">{formatCurrency(item.revenue)} DA</p>
                    <div className="mt-1 flex items-center justify-center space-x-1">
                      {index > 0 && monthlyTrends[index].revenue > monthlyTrends[index-1].revenue ? (
                        <ArrowTrendingUpIcon className="h-3 w-3 text-green-400" />
                      ) : index > 0 ? (
                        <ArrowTrendingDownIcon className="h-3 w-3 text-red-400" />
                      ) : null}
                      {index > 0 && (
                        <span className={monthlyTrends[index].revenue > monthlyTrends[index-1].revenue ? 'text-green-400' : 'text-red-400'}>
                          {(((monthlyTrends[index].revenue - monthlyTrends[index-1].revenue) / monthlyTrends[index-1].revenue) * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RevenueAnalysis; 